// Reactive data store with JSON Pointer-based path subscriptions

import { jsonPointerGet, jsonPointerSet, jsonPointerRemove } from '../utils/jsonPointer.ts';

function deepClone<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj));
}

export type DataModelListener = (path: string, value: unknown) => void;

export type DataModel = {
	get: (path: string) => unknown;
	remove: (path: string) => void;
	reset: (data: Record<string, unknown>) => void;
	set: (path: string, value: unknown) => void;
	snapshot: () => Record<string, unknown>;
	subscribe: (path: string, listener: DataModelListener) => () => void;
};

export function createDataModel(initialData: Record<string, unknown> = {}): DataModel {
	let data = deepClone(initialData);
	const listeners = new Map<string, Set<DataModelListener>>();

	function notifyListeners(changedPath: string, value: unknown) {
		// Notify exact match
		const exact = listeners.get(changedPath);
		if (exact) {
			for (const listener of exact) {
				listener(changedPath, value);
			}
		}

		// Notify ancestor paths (parent data changed)
		for (const [subscribedPath, subs] of listeners) {
			if (subscribedPath === changedPath) {
				continue;
			}
			// If the changed path is a parent of the subscribed path, notify with the sub-value
			if (subscribedPath.startsWith(changedPath + '/') || changedPath === '') {
				const subValue = jsonPointerGet(data, subscribedPath);
				for (const listener of subs) {
					listener(subscribedPath, subValue);
				}
			}
			// If the changed path is a child of the subscribed path, notify the parent
			if (changedPath.startsWith(subscribedPath + '/') || subscribedPath === '') {
				const parentValue = jsonPointerGet(data, subscribedPath);
				for (const listener of subs) {
					listener(subscribedPath, parentValue);
				}
			}
		}
	}

	return {
		get(path: string): unknown {
			if (path === '' || path === '/') {
				return path === '' ? data : data;
			}
			return jsonPointerGet(data, path);
		},

		set(path: string, value: unknown): void {
			data = jsonPointerSet(data, path, value) as Record<string, unknown>;
			notifyListeners(path, value);
		},

		remove(path: string): void {
			data = jsonPointerRemove(data, path) as Record<string, unknown>;
			notifyListeners(path, undefined);
		},

		subscribe(path: string, listener: DataModelListener): () => void {
			if (!listeners.has(path)) {
				listeners.set(path, new Set());
			}
			listeners.get(path)!.add(listener);
			return () => {
				const subs = listeners.get(path);
				if (subs) {
					subs.delete(listener);
					if (subs.size === 0) {
						listeners.delete(path);
					}
				}
			};
		},

		snapshot(): Record<string, unknown> {
			return deepClone(data);
		},

		reset(newData: Record<string, unknown>): void {
			data = deepClone(newData);
			notifyListeners('', data);
		},
	};
}
